import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import style from 'ember-style-modifier/modifiers/style';
import { eq } from 'tio-ui/utilities';
import { PhCheckCircle, PhXCircle, PhWarning, PhInfo, PhX } from 'ember-phosphor-icons';
import type { ToastIntent } from 'tio-ui/color-tokens';

// ---------------------------------------------------------------------------
// Toast item interface — shared between service and component
// ---------------------------------------------------------------------------

export interface ToastItem {
  id: string;
  message: string;
  intent: ToastIntent;
  duration: number;
  visible: boolean;
  /** Whether the close button is shown. @defaultValue true */
  allowClosing?: boolean;
  /** Optional action label (renders a clickable button on the toast) */
  actionLabel?: string;
  /** Callback when action is clicked */
  onAction?: () => void;
}

// ---------------------------------------------------------------------------
// Individual Toast component
// ---------------------------------------------------------------------------

interface SingleToastSignature {
  Args: {
    toast: ToastItem;
    onDismiss: (id: string) => void;
  };
  Element: HTMLDivElement;
}

class SingleToast extends Component<SingleToastSignature> {
  get showCloseButton() {
    return this.args.toast.allowClosing !== false;
  }

  get roleAttr() {
    return this.args.toast.intent === 'error' ? 'alert' : 'status';
  }

  get ariaLive() {
    return this.args.toast.intent === 'error' ? 'assertive' : 'polite';
  }

  get showProgress() {
    return this.args.toast.duration > 0;
  }

  handleDismiss = () => {
    this.args.onDismiss(this.args.toast.id);
  };

  handleAction = () => {
    this.args.toast.onAction?.();
    this.args.onDismiss(this.args.toast.id);
  };

  <template>
    <div
      data-test-toast
      data-test-toast-intent={{@toast.intent}}
      role={{this.roleAttr}}
      aria-live={{this.ariaLive}}
      class="app-toast app-toast--{{@toast.intent}} {{if @toast.visible 'app-toast--visible'}}"
      ...attributes
    >
      <div class="app-toast-body">
        {{! Intent icon — color comes from .app-toast-icon (var(--toast-accent)) }}
        <div class="app-toast-icon" data-test-toast-icon>
          {{#if (eq @toast.intent "success")}}
            <PhCheckCircle @size={{20}} />
          {{else if (eq @toast.intent "error")}}
            <PhXCircle @size={{20}} />
          {{else if (eq @toast.intent "warning")}}
            <PhWarning @size={{20}} />
          {{else}}
            <PhInfo @size={{20}} />
          {{/if}}
        </div>

        {{! Message }}
        <div class="app-toast-content">
          <p data-test-toast-message class="app-toast-message">
            {{@toast.message}}
          </p>
          {{#if @toast.actionLabel}}
            <button
              type="button"
              class="app-toast-action"
              data-test-toast-action
              {{on "click" this.handleAction}}
            >
              {{@toast.actionLabel}}
            </button>
          {{/if}}
        </div>

        {{! Close button (hidden when allowClosing is false) }}
        {{#if this.showCloseButton}}
          <button
            type="button"
            class="app-toast-close"
            data-test-toast-close
            aria-label="Dismiss"
            {{on "click" this.handleDismiss}}
          >
            <PhX @size={{14}} />
          </button>
        {{/if}}
      </div>

      {{! Success confetti burst — uses --tio-* palette (animation tokens live in styles.css). }}
      {{#if (eq @toast.intent "success")}}
        <div
          class="absolute top-2 left-6 pointer-events-none
            {{if @toast.visible 'reimagined-confetti' ''}}"
        >
          <span class="reimagined-confetti-dot reimagined-confetti-1 bg-emerald-400"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-2 bg-teal-400"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-3 bg-green-400"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-4 bg-emerald-300"></span>
          <span class="reimagined-confetti-dot reimagined-confetti-5 bg-cyan-400"></span>
        </div>
      {{/if}}

      {{! Auto-dismiss progress bar — fill color is --toast-accent. }}
      {{#if this.showProgress}}
        <div class="app-toast-progress-track">
          <div
            data-test-toast-progress
            class="app-toast-progress-fill {{if @toast.visible 'reimagined-toast-progress' ''}}"
            {{style animationDuration=(concat @toast.duration "ms")}}
          ></div>
        </div>
      {{/if}}
    </div>
  </template>
}

// ---------------------------------------------------------------------------
// ToastContainer – renders all active toasts, placed once in layout
// ---------------------------------------------------------------------------

interface ToastContainerSignature {
  Args: {
    /** Array of toast items to render (from the toast service) */
    toasts: ToastItem[];
    /** Called when a toast is dismissed */
    onDismiss: (id: string) => void;
  };
  Element: HTMLDivElement;
}

/**
 * ToastContainer – renders a stack of toast notifications.
 *
 * Place once in your app layout template. Bind to the toast service's
 * `toasts` array and `dismiss` method.
 *
 * Single toasts are styled by the `.app-toast*` semantic class family
 * in `addons/tio-ui/src/styles/`. Each intent class sets
 * two CSS custom properties — `--toast-accent` and `--toast-bg-tint`
 * — pulled from the canonical `--success/danger/warning/info-*`
 * recipes, and every visual element (left border, icon, progress bar
 * fill, bg wash) composes against those.
 *
 * @example
 * ```gts
 * import ToastContainer from 'tio-ui/components/toast';
 *
 * <ToastContainer @toasts={{this.toastService.toasts}} @onDismiss={{this.toastService.dismiss}} />
 * ```
 */
const ToastContainer: TOC<ToastContainerSignature> = <template>
  <div
    data-test-toast-container
    class="fixed bottom-6 right-6 z-[60] flex flex-col-reverse gap-3 pointer-events-none"
    role="region"
    aria-label="Notifications"
    ...attributes
  >
    {{#each @toasts as |toast|}}
      <div class="pointer-events-auto">
        <SingleToast @toast={{toast}} @onDismiss={{@onDismiss}} />
      </div>
    {{/each}}
  </div>
</template>;

export default ToastContainer;

export type { ToastContainerSignature, ToastIntent };
